import {Box} from '@primer/react'
import type {MarkdownEditorConfig} from '@github-ui/markdown-editor'
import {
  AddedToProjectEvent,
  MovedColumnsInProjectEvent,
  RemovedFromProjectEvent,
  SubscribedEvent,
  UnsubscribedEvent,
  MentionedEvent,
  ClosedEvent,
  ReopenedEvent,
  RenamedTitleEvent,
  LockedEvent,
  UnlockedEvent,
  PinnedEvent,
  UnpinnedEvent,
  LabeledEvent,
  UnlabeledEvent,
  UnassignedEvent,
  AssignedEvent,
  CommentDeletedEvent,
  UserBlockedEvent,
  MilestonedEvent,
  DemilestonedEvent,
  CrossReferencedEvent,
  ReferencedEvent,
  ConnectedEvent,
  TransferredEvent,
  ConvertedNoteToIssueEvent,
  DisconnectedEvent,
  MarkedAsDuplicateEvent,
  UnmarkedAsDuplicateEvent,
  ConvertedToDiscussionEvent,
  AddedToProjectV2Event,
  RemovedFromProjectV2Event,
  ProjectV2ItemStatusChangedEvent,
  ConvertedFromDraftEvent,
  VALUES,
  TimelineRowBorder,
  FallbackEvent,
} from '@github-ui/timeline-items'
import type {LockedEventViewer$key, ReferenceTypes, TimelineRowBorderCommentParams} from '@github-ui/timeline-items'
import {IssueComment} from '@github-ui/commenting'
import {createElement} from 'react'
import {graphql, useFragment} from 'react-relay'
import type {ReactionViewerUser$key} from '@github-ui/reaction-viewer'

import {TEST_IDS} from '../constants'
import type {TimelineItemsPaginated$key} from './__generated__/TimelineItemsPaginated.graphql'
import {getNumNextSameEvents, type TimelineItem} from '../utils/timeline-items'

export type HighlightedEvent = {
  id: string
  prefix: string
}

export type TimelineBaseProps = {
  issueRef?: LockedEventViewer$key
  viewer: ReactionViewerUser$key
  markdownEditorConfig?: MarkdownEditorConfig
  onCommentChange?: (id: string) => void
  onCommentEditCancel?: (id: string) => void
  onCommentReply: (quotedComment: string) => void
  onLinkClick?: (event: MouseEvent) => void
  navigate: (url: string) => void
  timelineEventBaseUrl?: string
  withLiveUpdates?: boolean
}

export type TimelineItemsProps = {
  issue: TimelineItemsPaginated$key
  highlightedCommentId?: string
  timelineEventBaseUrl?: string
  issueUrl: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  highlightedEvent?: HighlightedEvent
  type: string
} & TimelineBaseProps

type timelineProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryRef: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nextQueryRefs?: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  issueRef?: any
  issueUrl: string
  timelineEventBaseUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  referenceTypes?: ReferenceTypes
}

export const TIMELINE_ITEMS: Record<string, ((args: timelineProps) => JSX.Element) | undefined> = {
  AddedToProjectEvent,
  MovedColumnsInProjectEvent,
  RemovedFromProjectEvent,
  SubscribedEvent,
  UnsubscribedEvent,
  MentionedEvent,
  ClosedEvent,
  ReopenedEvent,
  RenamedTitleEvent,
  LockedEvent,
  UnlockedEvent,
  PinnedEvent,
  UnpinnedEvent,
  LabeledEvent,
  UnlabeledEvent,
  UnassignedEvent,
  AssignedEvent,
  CommentDeletedEvent,
  UserBlockedEvent,
  MilestonedEvent,
  DemilestonedEvent,
  CrossReferencedEvent,
  ReferencedEvent,
  ConnectedEvent,
  TransferredEvent,
  ConvertedNoteToIssueEvent,
  DisconnectedEvent,
  MarkedAsDuplicateEvent,
  UnmarkedAsDuplicateEvent,
  ConvertedToDiscussionEvent,
  AddedToProjectV2Event,
  RemovedFromProjectV2Event,
  ProjectV2ItemStatusChangedEvent,
  ConvertedFromDraftEvent,
}

export function TimelineItems({
  issue,
  issueRef,
  onCommentChange,
  onCommentEditCancel,
  onCommentReply,
  highlightedEvent,
  timelineEventBaseUrl,
  issueUrl,
  type,
  ...commentArgs
}: TimelineItemsProps) {
  const data = useFragment(
    graphql`
      fragment TimelineItemsPaginated on IssueTimelineItemsConnection {
        edges {
          node {
            ... on IssueComment {
              databaseId # this is required for highlighting support as this is a major event
            }
            ... on ClosedEvent {
              databaseId # this is required for highlighting support as this is a major event
            }
            ... on ReopenedEvent {
              databaseId # this is required for highlighting support as this is a major event
            }
            ... on ReferencedEvent {
              actor {
                login
              }
            }
            ... on CrossReferencedEvent {
              actor {
                login
              }
              source {
                __typename
              }
              willCloseTarget
            }
            ... on MentionedEvent {
              actor {
                login
              }
            }
            __id
            __typename
            ...IssueComment_issueComment
            ...ReactionViewerGroups
            ...AddedToProjectEvent
            ...MovedColumnsInProjectEvent
            ...RemovedFromProjectEvent
            ...SubscribedEvent
            ...UnsubscribedEvent
            ...MentionedEvent
            ...ClosedEvent
            ...ReopenedEvent
            ...LockedEvent
            ...UnlockedEvent
            ...PinnedEvent
            ...UnpinnedEvent
            ...LabeledEvent
            ...RenamedTitleEvent
            ...UnlabeledEvent
            ...UnassignedEvent
            ...AssignedEvent
            ...CommentDeletedEvent
            ...UserBlockedEvent
            ...MilestonedEvent
            ...DemilestonedEvent
            ...CrossReferencedEvent
            ...ReferencedEvent
            ...ConnectedEvent
            ...TransferredEvent
            ...ConvertedNoteToIssueEvent
            ...DisconnectedEvent
            ...MarkedAsDuplicateEvent
            ...UnmarkedAsDuplicateEvent
            ...ConvertedToDiscussionEvent
            ...AddedToProjectV2Event
            ...RemovedFromProjectV2Event
            ...ProjectV2ItemStatusChangedEvent
            ...ConvertedFromDraftEvent
          }
        }
      }
    `,
    issue,
  )

  const validTimelineItems = (data?.edges || []).flatMap(a => (a ? [a.node] : []))

  let skipTo = -1
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 2}} data-testid={TEST_IDS.issueTimeline(type)}>
      {validTimelineItems.map((item, index) => {
        // Skipping items that will be handled as inner elements of a previous item
        if (index <= skipTo) {
          return null
        }

        // Handling items we couldn't fetch
        if (!item) {
          return (
            <TimelineRowBorder
              key={`failed-timeline-item-${index}`}
              addDivider={true}
              item={{__id: `failed-timeline-item-${index}`}}
              isMajor={false}
              isHighlighted={false}
            >
              <FallbackEvent />
            </TimelineRowBorder>
          )
        }

        const highlightedEventId =
          item.__typename !== 'IssueComment' && highlightedEvent?.prefix !== 'issuecomment'
            ? highlightedEvent?.id
            : undefined
        const highlightedCommentId =
          item.__typename === 'IssueComment' && highlightedEvent?.prefix === 'issuecomment'
            ? highlightedEvent?.id
            : undefined

        const isMajorEvent = VALUES.timeline.majorEventTypes.includes(item.__typename)

        const addDivider = shouldAddDivider(validTimelineItems, index)

        // Checking if we can group consecutive items
        // If we can, pass them to the element being created and set skipTo so they will be skipped
        const {numEvents, referenceTypes} = getNumNextSameEvents(item.__typename, validTimelineItems, index)
        const nextItems = numEvents > 0 ? validTimelineItems.slice(index + 1, index + 1 + numEvents) : undefined
        skipTo = index + numEvents

        const timelineReactElement = TIMELINE_ITEMS[item.__typename]

        let itemToRender = null
        let commentParams: TimelineRowBorderCommentParams | undefined = undefined

        if (item.__typename === 'IssueComment') {
          commentParams = {
            first: index === 0 || validTimelineItems[index - 1]?.__typename !== 'IssueComment',
            last:
              index === validTimelineItems.length - 1 || validTimelineItems[index + 1]?.__typename !== 'IssueComment',
          }
          itemToRender = (
            <IssueComment
              comment={item}
              reactionGroups={item}
              onChange={() => onCommentChange && onCommentChange(item.__id)}
              onEditCancel={() => onCommentEditCancel && onCommentEditCancel(item.__id)}
              onReply={quotedComment => onCommentReply(quotedComment)}
              onSave={() => onCommentEditCancel && onCommentEditCancel(item.__id)}
              highlightedCommentId={highlightedCommentId}
              {...commentArgs}
            />
          )
        }

        if (timelineReactElement) {
          itemToRender = createElement(timelineReactElement, {
            queryRef: item,
            nextQueryRefs: nextItems,
            key: item.__id,
            issueRef,
            issueUrl,
            highlightedEventId,
            timelineEventBaseUrl: timelineEventBaseUrl || '/issues',
            refAttribute: commentArgs.refAttribute,
            referenceTypes,
          })
        }

        const isHighlighted = (highlightedCommentId || highlightedEventId) === String(item.databaseId)

        return (
          <TimelineRowBorder
            key={item.__id}
            addDivider={addDivider}
            item={item}
            isMajor={isMajorEvent && VALUES.timeline.borderedMajorEventTypes.includes(item.__typename)}
            isHighlighted={isHighlighted}
            commentParams={commentParams}
          >
            {itemToRender}
          </TimelineRowBorder>
        )
      })}
    </Box>
  )
}

function shouldAddDivider(items: TimelineItem[], index: number) {
  if (index > 0 && items[index - 1]) {
    const eventType = items[index]!.__typename
    const previousEventType = items[index - 1]!.__typename
    const isMajorEvent = VALUES.timeline.majorEventTypes.includes(eventType)
    const previousIsMajorEvent = VALUES.timeline.majorEventTypes.includes(previousEventType)

    if (isMajorEvent === previousIsMajorEvent) {
      if (eventType === 'IssueComment' && previousEventType === 'IssueComment') {
        return false
      } else if (eventType !== 'IssueComment' && previousEventType !== 'IssueComment') {
        return false
      }
    }
  }
  return true
}

try{ TIMELINE_ITEMS.displayName ||= 'TIMELINE_ITEMS' } catch {}
try{ TimelineItems.displayName ||= 'TimelineItems' } catch {}